export type FormFieldKind =
  | "short_text"
  | "long_text"
  | "email"
  | "phone"
  | "name"
  | "number"
  | "multiple_choice"
  | "checkboxes"
  | "dropdown"
  | "rating"
  | "file_upload"
  | "calculated"
  | "date"
  | "time"
  | "static_text"
  | "address";

export type FormSectionLayout = "single_column" | "two_column";

export interface FormSectionDefinition {
  id: string;
  title: string;
  description?: string;
  position: number;
  layout?: FormSectionLayout;
  parentSectionId?: string | null;
  settings?: Record<string, unknown>;
}

export interface FieldOptionDefinition {
  id: string;
  label: string;
  value?: string;
  description?: string;
  score?: number;
  nextSectionId?: string;
  metadata?: Record<string, unknown>;
}

export interface DynamicOptionsConfig {
  type: "kv" | "api" | "list";
  source: string;
  labelField?: string;
  valueField?: string;
  cacheTtlSeconds?: number;
  filters?: Array<{
    field: string;
    operator: "eq" | "neq" | "in" | "nin" | "contains" | "gt" | "gte" | "lt" | "lte";
    value: string | number | string[] | number[];
  }>;
}

export interface FieldScoringConfig {
  enabled: boolean;
  mode: "auto" | "manual";
  points: number;
  correctOptionIds?: string[];
  correctValue?: string | number;
  allowPartialCredit?: boolean;
}

export interface BaseFormField {
  id: string;
  type: FormFieldKind;
  title: string;
  description?: string;
  required?: boolean;
  sectionId?: string | null;
  position: number;
  tags?: string[];
  metadata?: Record<string, unknown>;
  scoring?: FieldScoringConfig;
  visibility?: "visible" | "hidden" | "read_only";
  /** Custom validation error message shown when field validation fails */
  validationMessage?: string;
}

export interface ShortTextFieldDefinition extends BaseFormField {
  type: "short_text";
  placeholder?: string;
  validation?: {
    minLength?: number;
    maxLength?: number;
    pattern?: string;
    patternDescription?: string;
  };
}

export interface LongTextFieldDefinition extends BaseFormField {
  type: "long_text";
  placeholder?: string;
  validation?: {
    minLength?: number;
    maxLength?: number;
  };
}

export interface EmailFieldDefinition extends BaseFormField {
  type: "email";
  placeholder?: string;
  validation?: {
    allowedDomains?: string[];
  };
}

export interface PhoneFieldDefinition extends BaseFormField {
  type: "phone";
  placeholder?: string;
  validation?: {
    countryCode?: string;
    format?: "international" | "national";
  };
}

export interface NameFieldDefinition extends BaseFormField {
  type: "name";
  placeholder?: string;
  showMiddleName?: boolean;
  showPrefix?: boolean;
  showSuffix?: boolean;
  validation?: {
    /** Custom regex pattern for name validation */
    pattern?: string;
    /** Description of the pattern for user-friendly error messages */
    patternDescription?: string;
    minLength?: number;
    maxLength?: number;
    /** If true, only allows Hindi and English characters (default: true) */
    allowHindiEnglishOnly?: boolean;
  };
}

export interface NumberFieldDefinition extends BaseFormField {
  type: "number";
  prefix?: string;
  suffix?: string;
  validation?: {
    min?: number;
    max?: number;
    step?: number;
    allowDecimals?: boolean;
  };
}

export interface RatingFieldDefinition extends BaseFormField {
  type: "rating";
  style: "stars" | "numbers" | "emojis";
  scale: number;
  lowLabel?: string;
  highLabel?: string;
}

export interface ChoiceFieldDefinition extends BaseFormField {
  type: "multiple_choice" | "checkboxes";
  options: FieldOptionDefinition[];
  allowOther?: boolean;
  randomizeOptions?: boolean;
  maxSelections?: number;
}

export interface DropdownFieldDefinition extends BaseFormField {
  type: "dropdown";
  options?: FieldOptionDefinition[];
  dynamicOptions?: DynamicOptionsConfig;
  allowMultiple?: boolean;
  allowSearch?: boolean;
}

export interface FileUploadFieldDefinition extends BaseFormField {
  type: "file_upload";
  maxFileSizeMB: number;
  maxFiles?: number;
  allowedTypes?: string[];
  storagePathTemplate?: string;
}

export interface DateFieldDefinition extends BaseFormField {
  type: "date";
  validation?: {
    min?: string;
    max?: string;
    includeTime?: boolean;
  };
}

export interface TimeFieldDefinition extends BaseFormField {
  type: "time";
  validation?: {
    timezoneMode?: "submitter" | "fixed";
    fixedTimezone?: string;
  };
}

export interface CalculatedFieldDefinition extends BaseFormField {
  type: "calculated";
  expression: string;
  inputFieldIds: string[];
  outputType: "number" | "text" | "boolean" | "date";
  precision?: number;
}

export interface StaticTextFieldDefinition extends BaseFormField {
  type: "static_text";
  content: string;
  format: "markdown" | "plain" | "html";
}

/** Indian States and Union Territories for address field */
export const INDIAN_STATES = [
  // States
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  // Union Territories
  "Andaman and Nicobar Islands",
  "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Jammu and Kashmir",
  "Ladakh",
  "Lakshadweep",
  "Puducherry",
] as const;

export type IndianState = typeof INDIAN_STATES[number];

/** Address data structure for address field values */
export interface AddressValue {
  houseNumber?: string;
  village?: string;
  post?: string;
  district?: string;
  state?: string;
  country?: string;
  pincode?: string;
}

export interface AddressFieldDefinition extends BaseFormField {
  type: "address";
  /** Default country value */
  defaultCountry?: string;
  /** Custom labels for sub-fields */
  labels?: {
    houseNumber?: string;
    village?: string;
    post?: string;
    district?: string;
    state?: string;
    country?: string;
    pincode?: string;
  };
  /** Placeholder texts for sub-fields */
  placeholders?: {
    houseNumber?: string;
    village?: string;
    post?: string;
    district?: string;
    pincode?: string;
  };
  /** Which sub-fields are required (all required by default if field.required is true) */
  requiredFields?: {
    houseNumber?: boolean;
    village?: boolean;
    post?: boolean;
    district?: boolean;
    state?: boolean;
    pincode?: boolean;
  };
}

export type FormFieldDefinition =
  | ShortTextFieldDefinition
  | LongTextFieldDefinition
  | EmailFieldDefinition
  | PhoneFieldDefinition
  | NameFieldDefinition
  | NumberFieldDefinition
  | RatingFieldDefinition
  | ChoiceFieldDefinition
  | DropdownFieldDefinition
  | FileUploadFieldDefinition
  | DateFieldDefinition
  | TimeFieldDefinition
  | CalculatedFieldDefinition
  | StaticTextFieldDefinition
  | AddressFieldDefinition;

export type ConditionOperator =
  | "equals"
  | "not_equals"
  | "contains"
  | "not_contains"
  | "greater_than"
  | "greater_than_or_equal"
  | "less_than"
  | "less_than_or_equal"
  | "is_empty"
  | "is_not_empty"
  | "in"
  | "not_in"
  | "between"
  | "starts_with"
  | "ends_with";

export interface ConditionalComparison {
  type: "comparison";
  fieldId: string;
  operator: ConditionOperator;
  value?:
  | string
  | number
  | boolean
  | Array<string | number | boolean>
  | { start?: string | number; end?: string | number };
}

export interface ConditionalGroup {
  type: "group";
  operator: "AND" | "OR";
  children: Array<ConditionalGroup | ConditionalComparison>;
}

export type ConditionalPredicate = ConditionalGroup | ConditionalComparison;

export type ConditionalAction =
  | { type: "show_field"; targetId: string }
  | { type: "hide_field"; targetId: string }
  | { type: "show_section"; targetId: string }
  | { type: "hide_section"; targetId: string }
  | { type: "jump_to_section"; targetId: string }
  | { type: "require_field"; targetId: string }
  | { type: "set_field_value"; targetId: string; value: unknown }
  | { type: "end_form" };

export interface ConditionalRule {
  id: string;
  name?: string;
  description?: string;
  priority: number;
  when: ConditionalPredicate;
  actions: ConditionalAction[];
  enabled: boolean;
}

/** Configuration for response deduplication */
export interface DeduplicationConfig {
  enabled: boolean;
  /** Method to identify unique respondents */
  mode: 'email' | 'phone' | 'ip' | 'device' | 'cookie' | 'email_or_phone';
  /** How to handle duplicate submissions */
  behavior?: 'block' | 'warn' | 'allow';
  /** Field ID to use for email/phone modes */
  fieldId?: string | null;
  /** Custom message shown when duplicate response detected */
  message?: string | null;
}

export interface FormSchemaSettings {
  progressBar?: boolean;
  allowSaveAndResume?: boolean;
  shuffleSections?: boolean;
  shuffleQuestions?: boolean;
  allowBackNavigation?: boolean;
  theme?: {
    primaryColor?: string;
    accentColor?: string;
    logoUrl?: string | null;
  };

  // Response Limits
  /** Maximum number of responses allowed (null = unlimited) */
  maxResponses?: number | null;

  // Scheduled Availability
  /** ISO date string when form opens for submissions */
  opensAt?: string | null;
  /** ISO date string when form closes for submissions */
  closesAt?: string | null;
  /** Custom message shown when form is closed */
  closedMessage?: string | null;

  // Response Deduplication
  /** Configuration for preventing duplicate submissions */
  deduplication?: DeduplicationConfig;
}

export interface FormSchemaV1 {
  version: "1.0";
  title?: string;
  description?: string;
  sections: FormSectionDefinition[];
  fields: FormFieldDefinition[];
  logicRules: ConditionalRule[];
  settings: FormSchemaSettings;
  metadata?: Record<string, unknown>;
}

export interface ValidationIssue {
  path: string;
  message: string;
  severity: "error" | "warning";
}

export interface ValidationResult {
  valid: boolean;
  issues: ValidationIssue[];
}

const FIELD_ID_PATTERN = /^[a-zA-Z0-9_-]+$/;
const MAX_UPLOAD_SIZE_MB = 50;

export function validateFormSchema(schema: FormSchemaV1): ValidationResult {
  const issues: ValidationIssue[] = [];

  if (schema.version !== "1.0") {
    issues.push({
      path: "version",
      message: "Unsupported schema version. Expected \"1.0\".",
      severity: "error",
    });
  }

  const sectionIds = new Set<string>();
  for (const section of schema.sections) {
    if (!section.id) {
      issues.push({
        path: "sections[].id",
        message: "Section id is required.",
        severity: "error",
      });
      continue;
    }
    if (sectionIds.has(section.id)) {
      issues.push({
        path: `sections.${section.id}`,
        message: "Duplicate section id detected.",
        severity: "error",
      });
    } else {
      sectionIds.add(section.id);
    }
    if (section.parentSectionId && !sectionIds.has(section.parentSectionId)) {
      issues.push({
        path: `sections.${section.id}.parentSectionId`,
        message: `Parent section ${section.parentSectionId} not found.`,
        severity: "error",
      });
    }
  }

  const fieldIds = new Set<string>();
  for (const field of schema.fields) {
    validateField(field, sectionIds, fieldIds, issues);
  }

  validateLogicRules(schema.logicRules, sectionIds, fieldIds, issues);

  return {
    valid: issues.every((issue) => issue.severity !== "error"),
    issues,
  };
}

function validateField(
  field: FormFieldDefinition,
  sectionIds: Set<string>,
  fieldIds: Set<string>,
  issues: ValidationIssue[]
): void {
  if (!field.id) {
    issues.push({
      path: "fields[].id",
      message: "Field id is required.",
      severity: "error",
    });
    return;
  }

  if (!FIELD_ID_PATTERN.test(field.id)) {
    issues.push({
      path: `fields.${field.id}.id`,
      message: "Field id must only contain alphanumeric characters, hyphens, or underscores.",
      severity: "error",
    });
  }

  if (fieldIds.has(field.id)) {
    issues.push({
      path: `fields.${field.id}`,
      message: "Duplicate field id detected.",
      severity: "error",
    });
  } else {
    fieldIds.add(field.id);
  }

  if (field.sectionId && !sectionIds.has(field.sectionId)) {
    issues.push({
      path: `fields.${field.id}.sectionId`,
      message: `Referenced section ${field.sectionId} does not exist.`,
      severity: "error",
    });
  }

  if (typeof field.position !== "number" || Number.isNaN(field.position)) {
    issues.push({
      path: `fields.${field.id}.position`,
      message: "Field position must be a number.",
      severity: "error",
    });
  }

  switch (field.type) {
    case "short_text":
      validateShortTextField(field, issues);
      break;
    case "long_text":
      validateLongTextField(field, issues);
      break;
    case "email":
      // Email fields have similar validation to short text
      break;
    case "phone":
      // Phone fields have format validation handled by client
      break;
    case "name":
      // Name fields have options (showMiddleName, etc) but no validation needed
      break;
    case "number":
      validateNumberField(field, issues);
      break;
    case "multiple_choice":
    case "checkboxes":
      validateChoiceField(field, issues);
      break;
    case "dropdown":
      validateDropdownField(field, issues);
      break;
    case "rating":
      validateRatingField(field, issues);
      break;
    case "file_upload":
      validateFileUploadField(field, issues);
      break;
    case "date":
      validateDateField(field, issues);
      break;
    case "time":
      validateTimeField(field, issues);
      break;
    case "calculated":
      validateCalculatedField(field, fieldIds, issues);
      break;
    case "static_text":
      validateStaticTextField(field, issues);
      break;
    case "address":
      // Address fields have validation handled by client-side
      break;
    default: {
      const fallbackId = (field as { id?: string }).id ?? "unknown";
      issues.push({
        path: `fields.${fallbackId}.type`,
        message: `Unsupported field type ${(field as { type: string }).type}.`,
        severity: "error",
      });
      break;
    }
  }

  if (field.scoring?.enabled) {
    if (field.scoring.points < 0) {
      issues.push({
        path: `fields.${field.id}.scoring.points`,
        message: "Scoring points must be a positive number.",
        severity: "error",
      });
    }
    if (field.type === "multiple_choice" || field.type === "checkboxes") {
      const choiceField = field as ChoiceFieldDefinition;
      const optionIds = new Set(choiceField.options.map((option) => option.id));
      for (const optionId of field.scoring.correctOptionIds ?? []) {
        if (!optionIds.has(optionId)) {
          issues.push({
            path: `fields.${field.id}.scoring.correctOptionIds`,
            message: `Correct option ${optionId} does not exist on the field.`,
            severity: "error",
          });
        }
      }
    }
  }
}

function validateShortTextField(field: ShortTextFieldDefinition, issues: ValidationIssue[]): void {
  const { validation } = field;
  if (!validation) {
    return;
  }
  if (
    typeof validation.minLength === "number" &&
    typeof validation.maxLength === "number" &&
    validation.minLength > validation.maxLength
  ) {
    issues.push({
      path: `fields.${field.id}.validation`,
      message: "minLength cannot be greater than maxLength.",
      severity: "error",
    });
  }
  if (validation.pattern) {
    try {
      RegExp(validation.pattern);
    } catch {
      issues.push({
        path: `fields.${field.id}.validation.pattern`,
        message: "Invalid regular expression pattern.",
        severity: "error",
      });
    }
  }
}

function validateLongTextField(field: LongTextFieldDefinition, issues: ValidationIssue[]): void {
  const { validation } = field;
  if (!validation) {
    return;
  }
  if (
    typeof validation.minLength === "number" &&
    typeof validation.maxLength === "number" &&
    validation.minLength > validation.maxLength
  ) {
    issues.push({
      path: `fields.${field.id}.validation`,
      message: "minLength cannot be greater than maxLength.",
      severity: "error",
    });
  }
}

function validateNumberField(field: NumberFieldDefinition, issues: ValidationIssue[]): void {
  const { validation } = field;
  if (!validation) {
    return;
  }
  if (typeof validation.step === "number" && validation.step <= 0) {
    issues.push({
      path: `fields.${field.id}.validation.step`,
      message: "Step must be greater than zero.",
      severity: "error",
    });
  }
  if (
    typeof validation.min === "number" &&
    typeof validation.max === "number" &&
    validation.min > validation.max
  ) {
    issues.push({
      path: `fields.${field.id}.validation`,
      message: "Min cannot be greater than max.",
      severity: "error",
    });
  }
}

function validateChoiceField(field: ChoiceFieldDefinition, issues: ValidationIssue[]): void {
  if (!Array.isArray(field.options) || field.options.length === 0) {
    issues.push({
      path: `fields.${field.id}.options`,
      message: "At least one option is required.",
      severity: "error",
    });
    return;
  }

  const optionIds = new Set<string>();
  for (const option of field.options) {
    if (!option.id) {
      issues.push({
        path: `fields.${field.id}.options`,
        message: "Each option must have an id.",
        severity: "error",
      });
      continue;
    }
    if (optionIds.has(option.id)) {
      issues.push({
        path: `fields.${field.id}.options.${option.id}`,
        message: "Duplicate option id detected.",
        severity: "error",
      });
    } else {
      optionIds.add(option.id);
    }
  }

  if (field.maxSelections && field.maxSelections < 1) {
    issues.push({
      path: `fields.${field.id}.maxSelections`,
      message: "maxSelections must be greater than zero.",
      severity: "error",
    });
  }
}

function validateDropdownField(field: DropdownFieldDefinition, issues: ValidationIssue[]): void {
  if ((!field.options || field.options.length === 0) && !field.dynamicOptions) {
    issues.push({
      path: `fields.${field.id}`,
      message: "Dropdown requires either static options or a dynamic options source.",
      severity: "error",
    });
  }

  if (field.dynamicOptions) {
    if (!field.dynamicOptions.source) {
      issues.push({
        path: `fields.${field.id}.dynamicOptions.source`,
        message: "Dynamic options source is required.",
        severity: "error",
      });
    }
    if (field.dynamicOptions.type === "api") {
      if (!field.dynamicOptions.labelField || !field.dynamicOptions.valueField) {
        issues.push({
          path: `fields.${field.id}.dynamicOptions`,
          message: "Dynamic API options require labelField and valueField.",
          severity: "error",
        });
      }
    }
  }

  if (field.options) {
    const optionIds = new Set<string>();
    for (const option of field.options) {
      if (!option.id) {
        issues.push({
          path: `fields.${field.id}.options`,
          message: "Each option must have an id.",
          severity: "error",
        });
        continue;
      }
      if (optionIds.has(option.id)) {
        issues.push({
          path: `fields.${field.id}.options.${option.id}`,
          message: "Duplicate option id detected.",
          severity: "error",
        });
      } else {
        optionIds.add(option.id);
      }
    }
  }
}

function validateRatingField(field: RatingFieldDefinition, issues: ValidationIssue[]): void {
  if (field.scale < 2 || field.scale > 10) {
    issues.push({
      path: `fields.${field.id}.scale`,
      message: "Rating scale must be between 2 and 10.",
      severity: "error",
    });
  }
}

function validateFileUploadField(field: FileUploadFieldDefinition, issues: ValidationIssue[]): void {
  if (field.maxFileSizeMB <= 0 || field.maxFileSizeMB > MAX_UPLOAD_SIZE_MB) {
    issues.push({
      path: `fields.${field.id}.maxFileSizeMB`,
      message: `Max file size must be between 1 and ${MAX_UPLOAD_SIZE_MB} MB.`,
      severity: "error",
    });
  }
  if (field.maxFiles && field.maxFiles < 1) {
    issues.push({
      path: `fields.${field.id}.maxFiles`,
      message: "maxFiles must be at least 1 when provided.",
      severity: "error",
    });
  }
  if (field.allowedTypes) {
    for (const type of field.allowedTypes) {
      if (!isValidMimeType(type)) {
        issues.push({
          path: `fields.${field.id}.allowedTypes`,
          message: `Invalid MIME type pattern: ${type}`,
          severity: "error",
        });
      }
    }
  }
}

function validateDateField(field: DateFieldDefinition, issues: ValidationIssue[]): void {
  const { validation } = field;
  if (!validation) {
    return;
  }
  if (validation.min && !isValidISODate(validation.min)) {
    issues.push({
      path: `fields.${field.id}.validation.min`,
      message: "Min date must be an ISO 8601 string.",
      severity: "error",
    });
  }
  if (validation.max && !isValidISODate(validation.max)) {
    issues.push({
      path: `fields.${field.id}.validation.max`,
      message: "Max date must be an ISO 8601 string.",
      severity: "error",
    });
  }
}

function validateTimeField(field: TimeFieldDefinition, issues: ValidationIssue[]): void {
  const { validation } = field;
  if (!validation) {
    return;
  }
  if (validation.timezoneMode === "fixed" && !validation.fixedTimezone) {
    issues.push({
      path: `fields.${field.id}.validation.fixedTimezone`,
      message: "Fixed timezone must be provided when timezoneMode is fixed.",
      severity: "error",
    });
  }
}

function validateCalculatedField(
  field: CalculatedFieldDefinition,
  fieldIds: Set<string>,
  issues: ValidationIssue[]
): void {
  if (!field.expression.trim()) {
    issues.push({
      path: `fields.${field.id}.expression`,
      message: "Calculated field expression must not be empty.",
      severity: "error",
    });
  }

  const dependencies = new Set(field.inputFieldIds);
  for (const dependency of dependencies) {
    if (!fieldIds.has(dependency) && dependency !== field.id) {
      issues.push({
        path: `fields.${field.id}.inputFieldIds`,
        message: `Calculated field references unknown field '${dependency}'.`,
        severity: "error",
      });
    }
    if (dependency === field.id) {
      issues.push({
        path: `fields.${field.id}.inputFieldIds`,
        message: "Calculated field cannot reference itself.",
        severity: "error",
      });
    }
  }
}

function validateStaticTextField(field: StaticTextFieldDefinition, issues: ValidationIssue[]): void {
  if (!field.content?.trim()) {
    issues.push({
      path: `fields.${field.id}.content`,
      message: "Static text content must not be empty.",
      severity: "error",
    });
  }
  if (!["markdown", "plain", "html"].includes(field.format)) {
    issues.push({
      path: `fields.${field.id}.format`,
      message: "Static text format must be markdown, plain, or html.",
      severity: "error",
    });
  }
}

function validateLogicRules(
  rules: ConditionalRule[],
  sectionIds: Set<string>,
  fieldIds: Set<string>,
  issues: ValidationIssue[]
): void {
  const ruleIds = new Set<string>();
  for (const rule of rules) {
    if (!rule.id) {
      issues.push({
        path: "logicRules[].id",
        message: "Logic rule id is required.",
        severity: "error",
      });
      continue;
    }
    if (ruleIds.has(rule.id)) {
      issues.push({
        path: `logicRules.${rule.id}`,
        message: "Duplicate logic rule id detected.",
        severity: "error",
      });
      continue;
    }
    ruleIds.add(rule.id);

    validatePredicate(rule.when, fieldIds, issues, `logicRules.${rule.id}.when`);
    for (const action of rule.actions) {
      validateAction(action, sectionIds, fieldIds, issues, `logicRules.${rule.id}.actions`);
    }
  }
}

function validatePredicate(
  predicate: ConditionalPredicate,
  fieldIds: Set<string>,
  issues: ValidationIssue[],
  path: string
): void {
  if (predicate.type === "comparison") {
    if (!fieldIds.has(predicate.fieldId)) {
      issues.push({
        path,
        message: `Logic predicate references unknown field '${predicate.fieldId}'.`,
        severity: "error",
      });
    }
  } else {
    if (!predicate.children || predicate.children.length === 0) {
      issues.push({
        path,
        message: "Logic group must contain at least one predicate.",
        severity: "error",
      });
    } else {
      for (const child of predicate.children) {
        validatePredicate(child, fieldIds, issues, path);
      }
    }
  }
}

function validateAction(
  action: ConditionalAction,
  sectionIds: Set<string>,
  fieldIds: Set<string>,
  issues: ValidationIssue[],
  path: string
): void {
  switch (action.type) {
    case "show_field":
    case "hide_field":
    case "require_field":
    case "set_field_value":
      if (!fieldIds.has(action.targetId)) {
        issues.push({
          path,
          message: `Logic action references unknown field '${action.targetId}'.`,
          severity: "error",
        });
      }
      break;
    case "show_section":
    case "hide_section":
    case "jump_to_section":
      if (!sectionIds.has(action.targetId)) {
        issues.push({
          path,
          message: `Logic action references unknown section '${action.targetId}'.`,
          severity: "error",
        });
      }
      break;
    case "end_form":
      break;
    default:
      issues.push({
        path,
        message: `Unsupported logic action ${(action as { type: string }).type}.`,
        severity: "error",
      });
  }
}

function isValidMimeType(value: string): boolean {
  // Allow wildcard subtype patterns such as image/* or explicit mime types.
  const mimePattern = /^[a-z0-9!#$&^_.+-]+\/([a-z0-9!#$&^_.+-]+|\*)$/i;
  const extensionPattern = /^\.[a-z0-9]+$/i;
  return mimePattern.test(value) || extensionPattern.test(value);
}

function isValidISODate(value: string): boolean {
  const date = new Date(value);
  return !Number.isNaN(date.getTime());
}
